import { DeleteOutlined } from '@ant-design/icons';
import { Form, Space } from 'antd';
import dayjs from 'dayjs';
import React, {
  type Key,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getOnlyAnatolyAccess } from '@app/access/utils';
import {
  useAddPaymentToPaymentAccountMutation,
  useDeletePaymentMutation,
  useLazyGetClientPaymentFiltersDataQuery,
  useLazyGetPaymentsSearchResultQuery,
  useSearchPaymentsMutation,
} from '@app/api/ClientPayments';
import type { PaymentSearchResult } from '@app/api/ClientPayments';
import {
  type ModelExpenseArticle,
  useLazyGetExpenseArticlesQuery,
  useUpdateExpenseArticleMutation,
} from '@app/api/ExpenseArticles';
import {
  useAddMoneyExpenseMutation,
  useDeleteMoneyExpenseMutation,
  useLazyGetFileQuery,
} from '@app/api/MoneyExpenses';
import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import {
  useLazyGetPaymentAccountsForSystemQuery,
  useMakeMoneyCollectionMutation,
} from '@app/api/PaymentAccounts';
import { MassActionsDropdown } from '@app/components';
import {
  FRONT_WARNING,
  RIGHTS_SALES_REGISTER,
  RIGHTS_IS_CREATE_COLLECTION,
  RIGHTS_IS_CREATE_EXPENSE,
  RIGHTS_IS_DELETE_COLLECTION,
  RIGHTS_IS_DELETE_EXPENSE,
  RIGHTS_IS_DELETE_SALE,
  RIGHTS_IS_ON,
  RIGHTS_IS_TOPPING_UP_CASH,
  TOOLBAR_SPACE,
  TOOLBAR_SPACE_HIDDEN,
  DATE_PICKER_FORMAT,
  ISO_FORMAT_INDEX,
  SUCCESS,
} from '@app/constants';
import {
  useAccessRights,
  useMemoizeObject,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { ClientPaymentsContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { WidthArchiveLabel } from '@app/shared/components';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NClientPayments } from '@app/types/ClientPayments';
import type { NTables } from '@app/types/Tables';
import { getMimeTypeByName } from '@app/utils';

import type { NClientPaymentsList } from '@pages/Clients/ClientPayments/ClientPaymentsList/ClientPaymentsList.types';
import { formatPaymentTotalAmount } from '@pages/Clients/ClientPayments/ClientPaymentsList/utils/formatPaymentTotalAmount';

import type { UploadFile } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import type { TableRowSelection } from 'antd/es/table/interface';

type GetSelectOptionsData =
  | {
      label: string;
      value: string | number;
      filterName?: string | null;
      IsDeleted?: boolean;
    }[]
  | null;

const { useForm, useWatch } = Form;

const today = dayjs(new Date());
const weekBeforeToday = dayjs(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
const INITIAL_ROWS_COUNT = 20;

const PLUS_OPERATION = 'plus';
const MINUS_OPERATION = 'minus';
const EMPTY_PAYMENTS: NClientPayments.ModelPaymentExpenseInfo[] = [];

export const useClientPaymentsList = () => {
  const { userId, database, activeBranchId } = useUserPrint();
  const onlyAnatolyAccess = getOnlyAnatolyAccess(database, userId);

  const [isLoading, setIsLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [paymentSearchResult, setPaymentSearchResult] = useState<{
    branchId: number | null;
    result: PaymentSearchResult;
  } | null>(null);
  const requestVersion = useRef(0);
  const pendingSearch = useRef(false);
  const currentBranchId = useRef(activeBranchId);
  currentBranchId.current = activeBranchId;
  const visibleResult =
    paymentSearchResult?.branchId === activeBranchId
      ? paymentSearchResult.result
      : null;
  const payments = visibleResult?.Items ?? EMPTY_PAYMENTS;
  const totalRowsCount = visibleResult?.CountItems ?? 0;
  const totalAmount = formatPaymentTotalAmount(visibleResult?.TotalAmount);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_COUNT);
  const [toolbarClass, setToolbarClass] = useState(TOOLBAR_SPACE);
  const [searchError, setSearchError] = useState('');
  const [filtersData, setFiltersData] =
    useState<NClientPayments.ModelClientPaymentFiltersData>();
  const [isFiltersHidden, setIsFiltersHidden] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);

  const [selectedPayTypes, setSelectedPayTypes] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedUsers, setSelectedUsers] = useState<BaseOptionType[] | null>(
    null
  );
  const [selectedPaymentAccounts, setSelectedPaymentAccounts] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedLegalEntities, setSelectedLegalEntities] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedPaymentSystems, setSelectedPaymentSystems] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedOperations, setSelectedOperations] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedArticles, setSelectedArticles] = useState<
    BaseOptionType[] | null
  >(null);

  const [paymentAccounts, setPaymentAccounts] = useState<ModelPaymentAccount[]>(
    []
  );
  const [expenseArticles, setExpenseArticles] = useState<ModelExpenseArticle[]>(
    []
  );
  const [selectedPaymentAccountId, setSelectedPaymentAccountId] = useState(0);
  const [selectionCount, setSelectionCount] = useState(0);
  const [selectedRowsIds, setSelectedRowsIds] = useState<Key[]>([]);
  const [isArchivingModalOpen, setIsArchivingModalOpen] = useState(false);
  const [deletionLoader, setDeletionLoader] = useState(false);
  const [paymentInfoModalOpen, setPaymentInfoModalOpen] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const {
    isPaymentsModalOpen,
    isCollectionsModalOpen,
    isExpensesModalOpen,
    setIsPaymentsModalOpen,
    setIsCollectionsModalOpen,
    setIsExpensesModalOpen,
    searchId,
    setSearchId,
  } = useContext(ClientPaymentsContext);

  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= 550;

  const { hasUserRight: hasUserRightForModuleView } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_ON
  );
  const { hasUserRight: hasUserRightToCreateExpense } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_CREATE_EXPENSE
  );
  const { hasUserRight: hasUserRightToCreateCollection } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_CREATE_COLLECTION
  );
  const { hasUserRight: hasUserRightToToppingUpRegister } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_TOPPING_UP_CASH
  );
  const { hasUserRight: hasUserRightToDeleteExpense } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_DELETE_EXPENSE
  );
  const { hasUserRight: hasUserRightToDeleteCollection } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_DELETE_COLLECTION
  );
  const { hasUserRight: hasUserRightToDeleteSale } = useAccessRights(
    RIGHTS_SALES_REGISTER,
    RIGHTS_IS_DELETE_SALE
  );
  const [form] = useForm();
  const [addPaymentsForm] = useForm();
  const [addCollectionsForm] = useForm();
  const [addCExpensesForm] = useForm();

  const files = useWatch('Files', addCExpensesForm);

  const { showNotification } = useNotification();

  const [getFile] = useLazyGetFileQuery();
  const [addMoneyExpense] = useAddMoneyExpenseMutation();
  const [deleteMoneyExpense] = useDeleteMoneyExpenseMutation();
  const [getExpenseArticles] = useLazyGetExpenseArticlesQuery();
  const [updateExpenseArticle] = useUpdateExpenseArticleMutation();
  const [getPaymentAccountsForSystem] =
    useLazyGetPaymentAccountsForSystemQuery();
  const [makeMoneyCollection] = useMakeMoneyCollectionMutation();
  const [searchPayments] = useSearchPaymentsMutation();
  const [getClientPaymentFiltersData] =
    useLazyGetClientPaymentFiltersDataQuery();
  const [getPaymentsSearchResult] = useLazyGetPaymentsSearchResultQuery();
  const [addPaymentToPaymentAccount] = useAddPaymentToPaymentAccountMutation();
  const [deletePayment] = useDeletePaymentMutation();

  const isDisabledDeleteButton = useMemo(
    () =>
      !(
        selectionCount > 0 &&
        (hasUserRightToDeleteExpense ||
          hasUserRightToDeleteCollection ||
          hasUserRightToDeleteSale)
      ),
    [
      selectionCount,
      hasUserRightToDeleteCollection,
      hasUserRightToDeleteExpense,
      hasUserRightToDeleteSale,
    ]
  );
  const showMassActionButton = useMemo(
    () =>
      hasUserRightToDeleteExpense ||
      hasUserRightToDeleteCollection ||
      hasUserRightToDeleteSale,
    [
      hasUserRightToDeleteCollection,
      hasUserRightToDeleteExpense,
      hasUserRightToDeleteSale,
    ]
  );

  const items = useMemo(
    () => [
      {
        key: '1',
        label: (
          <Space
            onClick={() => {
              if (!isDisabledDeleteButton) {
                setIsArchivingModalOpen(true);
              }
            }}
            data-testid="delete-from-database"
          >
            <DeleteOutlined />
            Удалить из базы
          </Space>
        ),
        disabled: isDisabledDeleteButton,
      },
    ],
    [isDisabledDeleteButton]
  );

  const MassActionButton = useMemo(
    () =>
      showMassActionButton ? (
        <div className="mass-actions" data-testid="actions-button">
          <MassActionsDropdown
            items={items}
            data={payments}
            selectionCount={selectionCount}
          />
        </div>
      ) : (
        <></>
      ),
    [showMassActionButton, selectionCount, items, payments]
  );

  const rowSelection: TableRowSelection<NTables.ClientPayments> | undefined =
    useMemoizeObject({
      onChange: (
        selectedRowKeys: Key[],
        selectedRows: NTables.ClientPayments[]
      ) => {
        setSelectionCount(selectedRows.length);
        setSelectedRowsIds(selectedRowKeys);
      },
      renderCell: (checked, record, index, originNode) => {
        return React.cloneElement(originNode as React.ReactElement, {
          'data-testid': 'operation-checkbox',
        });
      },
      columnTitle: (checkboxNode: React.ReactNode) =>
        generateSpanWithDataTestId(checkboxNode, 'all-operations-checkbox'),
    });

  const handleFileClick = useCallback(
    async (fileId: number) => {
      try {
        const { Data, Name } = await getFile({
          fileId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        if (!Data || !Name) {
          showNotification('Файл не найден', FRONT_WARNING);
          return;
        }

        const byteCharacters = atob(Data);
        const byteNumbers = new Uint8Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const blob = new Blob([byteNumbers], {
          type: 'application/octet-stream',
        });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = Name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [activeBranchId, getFile, showNotification]
  );

  const handleFileOpen = useCallback(
    async (fileId: number) => {
      try {
        const { Data, Name } = await getFile({
          fileId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        if (!Data || !Name) {
          showNotification('Файл не найден', FRONT_WARNING);
          return;
        }

        const byteCharacters = atob(Data);
        const byteNumbers = new Uint8Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const mimeType = getMimeTypeByName(Name);

        const blob = new Blob([byteNumbers], { type: mimeType });
        const url = URL.createObjectURL(blob);

        window.open(url, '_blank', 'noopener,noreferrer');

        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [activeBranchId, getFile, showNotification]
  );

  const hideToolbar = useCallback(
    () => setToolbarClass(TOOLBAR_SPACE_HIDDEN),
    []
  );

  const hideFilters = useCallback(() => {
    if (isFiltersHidden) {
      setToolbarClass(TOOLBAR_SPACE);
    } else {
      hideToolbar();
    }
    setIsFiltersHidden((pervState) => !pervState);
  }, [isFiltersHidden, hideToolbar]);

  const beginSearchRequest = useCallback((isSearch: boolean) => {
    const version = ++requestVersion.current;
    pendingSearch.current = isSearch;
    setIsLoading(true);
    setPaymentSearchResult((previous) =>
      previous
        ? { ...previous, result: { ...previous.result, TotalAmount: null } }
        : null
    );
    return version;
  }, []);

  const isCurrentRequest = useCallback(
    (version: number) =>
      version === requestVersion.current &&
      activeBranchId === currentBranchId.current,
    [activeBranchId]
  );

  const finishSearchingByPayments = useCallback(
    (result: PaymentSearchResult, isPageNumberSetting = false) => {
      setSearchId(result.Id);
      setPaymentSearchResult({ branchId: activeBranchId, result });
      setPageNumber(isPageNumberSetting ? 1 : result.PageN);
    },
    [activeBranchId, setSearchId]
  );

  const getPaymentAccountsForSystemHandler = useCallback(async () => {
    try {
      const paymentAccountsResult = await getPaymentAccountsForSystem({
        ActiveBranchId: activeBranchId,
        branchId: activeBranchId,
        includeDeleted: false,
      }).unwrap();

      setPaymentAccounts(paymentAccountsResult);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [activeBranchId, getPaymentAccountsForSystem, showNotification]);

  const initialSearch = useCallback(async () => {
    const version = beginSearchRequest(true);
    let isLoadingDictionaries = false;
    try {
      const result = await searchPayments({
        ActiveBranchId: activeBranchId,
        data: {
          SearchTerm: '',
          DateS: weekBeforeToday.format('YYYY-MM-DD'),
          DateE: today.format('YYYY-MM-DD'),
          ClientIds: null,
          UserIds: null,
          PaymentAccountIds: null,
          LegalEntityIds: null,
          PayTypes: null,
          ItemsPerPage: INITIAL_ROWS_COUNT,
          Operations: null,
          ArticleIds: null,
          PaymentSystems: null,
        },
      }).unwrap();

      if (activeBranchId !== currentBranchId.current) return;
      if (isCurrentRequest(version)) finishSearchingByPayments(result);

      isLoadingDictionaries = true;
      const filtersDataResult = await getClientPaymentFiltersData({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      if (activeBranchId !== currentBranchId.current) return;
      setFiltersData(filtersDataResult);

      if (activeBranchId) {
        getPaymentAccountsForSystemHandler();

        const expenseArticlesResult = await getExpenseArticles({
          isOnlyActive: true,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        if (activeBranchId === currentBranchId.current) {
          setExpenseArticles(expenseArticlesResult);
        }
      }
    } catch (error) {
      if (
        isCurrentRequest(version) ||
        (isLoadingDictionaries && activeBranchId === currentBranchId.current)
      ) {
        showNotification(error);
      }
    } finally {
      if (isCurrentRequest(version)) {
        pendingSearch.current = false;
        setIsLoading(false);
      }
    }
    // finishSearchingByPayments дублирует вызов searchPayments при нажатии на Enter
    // eslint-disable-next-line
  }, [activeBranchId, showNotification]);

  const submitForm = useCallback(async () => {
    const { Search, Date } = form.getFieldsValue(['Search', 'Date']);
    const SearchTerm = Search ? Search : '';
    const hasCorrectSearchTerm =
      (SearchTerm && SearchTerm.length >= 3) || !SearchTerm;

    if (!hasCorrectSearchTerm) return;

    const DateS =
      Date && Date[0]
        ? Date[0].format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
        : null;
    const DateE =
      Date && Date[1]
        ? Date[1].format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
        : null;

    const getIds = (data: BaseOptionType[] | null | undefined) =>
      data?.length ? data.map(({ value }) => value) : null;

    const version = beginSearchRequest(true);
    try {
      const result = await searchPayments({
        ActiveBranchId: activeBranchId,
        data: {
          SearchTerm: SearchTerm,
          DateS,
          DateE,
          ClientIds: null,
          UserIds: getIds(selectedUsers),
          PaymentAccountIds: getIds(selectedPaymentAccounts),
          LegalEntityIds: getIds(selectedLegalEntities),
          PayTypes: getIds(selectedPayTypes),
          ItemsPerPage: rowsPerPage,
          Operations: getIds(selectedOperations),
          ArticleIds: getIds(selectedArticles),
          PaymentSystems: getIds(selectedPaymentSystems),
        },
      }).unwrap();

      if (!isCurrentRequest(version)) return;
      finishSearchingByPayments(result, true);
    } catch (error) {
      if (isCurrentRequest(version)) showNotification(error);
    } finally {
      if (isCurrentRequest(version)) {
        pendingSearch.current = false;
        setIsLoading(false);
      }
    }
  }, [
    form,
    activeBranchId,
    selectedUsers,
    selectedPaymentAccounts,
    selectedLegalEntities,
    selectedPayTypes,
    rowsPerPage,
    selectedOperations,
    selectedArticles,
    selectedPaymentSystems,
    finishSearchingByPayments,
    beginSearchRequest,
    isCurrentRequest,
    searchPayments,
    showNotification,
  ]);

  const handlePageChange = useCallback(
    async (page: number, pageSize: number) => {
      document.querySelector('.page-content')?.scrollTo(0, 0);

      if (
        searchId &&
        paymentSearchResult?.branchId === activeBranchId &&
        !pendingSearch.current
      ) {
        const version = beginSearchRequest(false);
        try {
          const result = await getPaymentsSearchResult({
            ActiveBranchId: activeBranchId,
            searchId,
            page: page,
            rowsPerPage: pageSize,
          }).unwrap();

          if (!isCurrentRequest(version)) return;
          finishSearchingByPayments(result);
          setRowsPerPage(pageSize);
        } catch (error) {
          if (isCurrentRequest(version)) showNotification(error);
        } finally {
          if (isCurrentRequest(version)) setIsLoading(false);
        }
      }
    },
    [
      activeBranchId,
      getPaymentsSearchResult,
      searchId,
      paymentSearchResult?.branchId,
      showNotification,
      beginSearchRequest,
      isCurrentRequest,
      finishSearchingByPayments,
    ]
  );

  const handleReset = useCallback(async () => {
    const version = beginSearchRequest(true);

    form.resetFields();
    setSelectedPayTypes([]);
    setSelectedUsers([]);
    setSelectedPaymentAccounts([]);
    setSelectedLegalEntities([]);
    setSelectedOperations([]);
    setSelectedArticles([]);
    setSelectedPaymentSystems([]);
    setSearchId(null);
    setRowsPerPage(INITIAL_ROWS_COUNT);

    try {
      const result = await searchPayments({
        ActiveBranchId: activeBranchId,
        data: {
          SearchTerm: '',
          DateS: weekBeforeToday.format('YYYY-MM-DD'),
          DateE: today.format('YYYY-MM-DD'),
          ClientIds: null,
          UserIds: null,
          PaymentAccountIds: null,
          LegalEntityIds: null,
          PayTypes: null,
          ItemsPerPage: INITIAL_ROWS_COUNT,
          Operations: null,
          ArticleIds: null,
          PaymentSystems: null,
        },
      }).unwrap();

      if (!isCurrentRequest(version)) return;
      finishSearchingByPayments(result, true);
    } catch (error) {
      if (isCurrentRequest(version)) showNotification(error);
    } finally {
      if (isCurrentRequest(version)) {
        pendingSearch.current = false;
        setIsLoading(false);
      }
    }
  }, [
    form,
    setSearchId,
    activeBranchId,
    finishSearchingByPayments,
    beginSearchRequest,
    isCurrentRequest,
    searchPayments,
    showNotification,
  ]);

  const replaceCommaWithDot = useCallback((inputString: string) => {
    const resultString = inputString.replace(/,/g, '.');
    return resultString;
  }, []);

  const getAmount = useCallback(
    (amountString: string) => {
      const amount = Number(
        amountString.replace(/\s/g, '').indexOf(',') !== -1
          ? replaceCommaWithDot(amountString.replace(/\s/g, ''))
          : amountString.replace(/\s/g, '')
      );

      return amount;
    },
    [replaceCommaWithDot]
  );

  const updateBalance = useCallback(
    (
      PaymentAccounts: number,
      handledAmount: number,
      operationType: typeof PLUS_OPERATION | typeof MINUS_OPERATION
    ): void => {
      const isPlusOperation = operationType === PLUS_OPERATION;
      const isMinusOperation = operationType === MINUS_OPERATION;
      const paymentAccountsWithoutActive =
        paymentAccounts.filter(({ Id }) => Id !== PaymentAccounts) || [];
      const activePaymentData = paymentAccounts.find(
        ({ Id }) => Id === PaymentAccounts
      );

      if (!activePaymentData) {
        return window.location.reload();
      }

      let updatedBalance = activePaymentData.Balance;

      if (isPlusOperation) {
        updatedBalance += handledAmount;
      }

      if (isMinusOperation) {
        updatedBalance -= handledAmount;
      }

      setPaymentAccounts([
        ...paymentAccountsWithoutActive,
        ...(activePaymentData
          ? [
              {
                ...activePaymentData,
                Balance: updatedBalance,
              },
            ]
          : []),
      ]);
    },
    [paymentAccounts]
  );

  const submitPayment: NClientPaymentsList.GeneralFunctionsResult = useCallback(
    async ({ Amount, Description, PaymentAccounts }) => {
      const handledAmount = getAmount(Amount);

      setIsModalLoading(true);

      try {
        await addPaymentToPaymentAccount({
          ActiveBranchId: activeBranchId,
          data: {
            AccountId: PaymentAccounts,
            Amount: handledAmount,
            Comment: Description,
          },
        }).unwrap();

        showNotification('', SUCCESS);

        addPaymentsForm.resetFields();
        setSelectedPaymentAccountId(0);
        form.submit();
        updateBalance(PaymentAccounts, handledAmount, PLUS_OPERATION);
      } catch (error) {
        showNotification(error);
      } finally {
        setIsModalLoading(false);
        setIsPaymentsModalOpen(false);
        setSelectedPaymentAccountId(0);
      }
    },
    [
      activeBranchId,
      addPaymentToPaymentAccount,
      addPaymentsForm,
      form,
      getAmount,
      showNotification,
      updateBalance,
      setIsPaymentsModalOpen,
    ]
  );

  const submitCollection: NClientPaymentsList.GeneralFunctionsResult =
    useCallback(
      async ({ Amount, Description, PaymentAccounts }) => {
        const amount = getAmount(Amount);

        setIsModalLoading(true);
        try {
          await makeMoneyCollection({
            ActiveBranchId: activeBranchId,
            paymentAccountId: PaymentAccounts,
            amount,
            comment: Description,
          }).unwrap();

          showNotification('', SUCCESS);
          addCollectionsForm.resetFields();
          setSelectedPaymentAccountId(0);
          form.submit();
          updateBalance(PaymentAccounts, amount, MINUS_OPERATION);
        } catch (error) {
          showNotification(error);
        } finally {
          setIsModalLoading(false);
          setIsCollectionsModalOpen(false);
          setSelectedPaymentAccountId(0);
        }
      },
      [
        activeBranchId,
        makeMoneyCollection,
        addCollectionsForm,
        form,
        getAmount,
        showNotification,
        updateBalance,
        setIsCollectionsModalOpen,
      ]
    );

  const convertFilesToFormData = (files: UploadFile[]): FormData => {
    const formData = new FormData();

    files.forEach((file) => {
      if (file.originFileObj) {
        formData.append('Attachments', file.originFileObj as File);
      }
    });

    return formData;
  };

  const saveExpense = useCallback(
    async (
      {
        PaymentAccounts,
        Description,
        TimeCreated,
      }: NClientPaymentsList.GeneralFieldValues,
      ExpenseArticleId: number | undefined,
      amount: number
    ) => {
      try {
        setIsModalLoading(true);

        if (!ExpenseArticleId)
          return showNotification(
            'Идентификатор статьи расхода не найден',
            FRONT_WARNING
          );

        const Attachments = files?.length
          ? await convertFilesToFormData(files)
          : null;

        await addMoneyExpense({
          ActiveBranchId: activeBranchId,
          data: Attachments ?? new FormData(),
          PaymentAccountId: PaymentAccounts,
          ExpenseArticleId,
          Amount: amount,
          Comment: Description,
          TimeCreated: TimeCreated
            ? dayjs(TimeCreated).format('YYYY-MM-DDTHH:mm:ss')
            : undefined,
        }).unwrap();

        showNotification('', SUCCESS);
        addCExpensesForm.resetFields();
        setSelectedPaymentAccountId(0);
        updateBalance(PaymentAccounts, amount, MINUS_OPERATION);

        await initialSearch();
      } catch (error) {
        showNotification(error);
      } finally {
        setIsModalLoading(false);
        setIsExpensesModalOpen(false);
        setSelectedPaymentAccountId(0);
      }
    },
    [
      files,
      showNotification,
      activeBranchId,
      addCExpensesForm,
      updateBalance,
      initialSearch,
      setIsExpensesModalOpen,
      addMoneyExpense,
    ]
  );

  const deletePayments = useCallback(
    async (id: number) => {
      try {
        await deletePayment({
          ActiveBranchId: activeBranchId,
          paymentId: id,
        }).unwrap();

        showNotification('', SUCCESS);
      } catch (error) {
        showNotification(error);
      }
    },
    [activeBranchId, deletePayment, showNotification]
  );

  const deleteExpenses = useCallback(
    async (id: number) => {
      try {
        await deleteMoneyExpense({
          ActiveBranchId: activeBranchId,
          moneyExpenseId: id,
        }).unwrap();

        await getPaymentAccountsForSystemHandler();

        showNotification('', SUCCESS);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      activeBranchId,
      deleteMoneyExpense,
      getPaymentAccountsForSystemHandler,
      showNotification,
    ]
  );

  const handleDeleteSelectingPayments = useCallback(
    async (selectedPayments: NClientPayments.ModelPaymentExpenseInfo[]) => {
      selectedPayments.forEach(({ IsPayment, ObjectId }) => {
        if (!ObjectId)
          return showNotification(
            `Не найден платежный аккаунт с ID ${ObjectId}`,
            FRONT_WARNING
          );

        if (IsPayment) {
          deletePayments(ObjectId);
        } else {
          deleteExpenses(ObjectId);
        }
      });
    },
    [deleteExpenses, deletePayments, showNotification]
  );

  const deleteRows = useCallback(async () => {
    setDeletionLoader(true);

    const selectedPayments = payments.filter(
      ({ ObjectId, IsPayment, IsCollection }) =>
        selectedRowsIds.includes(`${ObjectId}-${IsPayment}-${IsCollection}`)
    );

    try {
      await handleDeleteSelectingPayments(selectedPayments);
    } catch (error) {
      showNotification(error);
    } finally {
      setDeletionLoader(false);
      setIsArchivingModalOpen(false);
      form.submit();
    }
  }, [
    form,
    payments,
    selectedRowsIds,
    showNotification,
    handleDeleteSelectingPayments,
  ]);

  const submitExpense: NClientPaymentsList.GeneralFunctionsResult = useCallback(
    async (values) => {
      const { Amount, Expenses } = values;
      const amount = getAmount(Amount);
      const existingExpense = expenseArticles.find(
        ({ Id }) => `${Id}` === `${Expenses}`
      );

      setIsModalLoading(true);

      if (!existingExpense) {
        try {
          const { Id } = await updateExpenseArticle({
            ActiveBranchId: activeBranchId,
            data: {
              Name: Expenses,
              IsDeleted: false,
              Description: '',
            },
          }).unwrap();

          await saveExpense(values, Id, amount);

          form.submit();
          return;
        } catch (error) {
          showNotification(error);
          return;
        } finally {
          setIsModalLoading(false);
        }
      }

      try {
        const { Id } = existingExpense;

        await saveExpense(values, Id, amount);
      } catch (error) {
        showNotification(error);
      } finally {
        setIsModalLoading(false);
      }
    },
    [
      expenseArticles,
      form,
      getAmount,
      saveExpense,
      showNotification,
      activeBranchId,
      updateExpenseArticle,
    ]
  );

  const getSelectOptions = useCallback(
    (data: GetSelectOptionsData, deleteItemDataTestId?: string) => {
      if (!data?.length) return [];

      return data
        .sort((a, b) => {
          const isADeleted = a.IsDeleted || false;
          const isBDeleted = b.IsDeleted || false;

          if (isADeleted !== isBDeleted) {
            return isADeleted ? 1 : -1;
          }
          return a?.label?.localeCompare(b?.label ?? '') || 0;
        })
        .map(({ label, filterName, IsDeleted, value }) => ({
          label: (
            <WidthArchiveLabel
              title={label}
              isDeleted={IsDeleted}
              deleteItemDataTestId={deleteItemDataTestId}
            />
          ),
          value: value,
          filterName: filterName,
        }));
    },
    []
  );

  const getUsersSelectOptions = useCallback(() => {
    if (!filtersData?.Users?.length) return [];
    const { Users } = filtersData;

    const data = Users.map((user) => {
      const label = `${user?.Name}`?.trim();

      return {
        label,
        value: user?.Id,
        filterName: label,
        IsDeleted:
          'IsDeleted' in user && typeof user?.IsDeleted === 'boolean'
            ? user?.IsDeleted
            : false,
      };
    });

    return getSelectOptions(data, 'archive-user');
  }, [filtersData, getSelectOptions]);

  const getPaymentAccountsSelectOptions = useCallback(() => {
    if (!filtersData?.PaymentAccounts?.length) return [];
    const { PaymentAccounts } = filtersData;

    const data = PaymentAccounts.map(({ Name, Id, ...item }) => {
      const label = `${Name}`?.trim();

      return {
        label,
        value: Id,
        filterName: label,
        IsDeleted: item?.IsDeleted ?? false,
      };
    });

    return getSelectOptions(data, 'archive-cash-register');
  }, [filtersData, getSelectOptions]);

  const getLegalEntitiesSelectOptions = useCallback(() => {
    if (!filtersData?.LegalEntities?.length) return [];
    const { LegalEntities } = filtersData;

    const data = LegalEntities.map(({ Name, Id, ...item }) => {
      const label = `${Name}`?.trim();

      return {
        label,
        value: Id,
        filterName: label,
        IsDeleted: item?.IsDeleted ?? false,
      };
    });

    return getSelectOptions(data, 'archive-legal-entity');
  }, [filtersData, getSelectOptions]);

  const getExpenseArticlesSelectOptions = useCallback(() => {
    if (!filtersData?.ExpenseArticles?.length) return [];
    const { ExpenseArticles } = filtersData;

    const data = ExpenseArticles.map(({ Name, Id, ...item }) => {
      const label = `${Name}`?.trim();

      return {
        label,
        value: Id,
        filterName: label,
        IsDeleted: item?.IsDeleted ?? false,
      };
    });

    return getSelectOptions(data);
  }, [filtersData, getSelectOptions]);

  const expenseArticlesSelectOptions = useMemo(
    getExpenseArticlesSelectOptions,
    [getExpenseArticlesSelectOptions]
  );

  const legalEntitiesSelectOptions = useMemo(getLegalEntitiesSelectOptions, [
    getLegalEntitiesSelectOptions,
  ]);

  const paymentAccountsSelectOptions = useMemo(
    getPaymentAccountsSelectOptions,
    [getPaymentAccountsSelectOptions]
  );

  const usersSelectOptions = useMemo(getUsersSelectOptions, [
    getUsersSelectOptions,
  ]);

  const handlePaymentInfoModalOpen = useCallback(
    (paymentId: number | null, isExpense: boolean) => {
      if (isExpense) return;

      setObjectId(paymentId);
      setPaymentInfoModalOpen(true);
    },
    []
  );

  return {
    isLoading,
    payments,
    form,
    toolbarClass,
    isMobile,
    searchError,
    setSearchError,
    selectedPayTypes,
    setSelectedPayTypes,
    selectedUsers,
    setSelectedUsers,
    selectedPaymentAccounts,
    setSelectedPaymentAccounts,
    selectedLegalEntities,
    setSelectedLegalEntities,
    selectedOperations,
    setSelectedOperations,
    selectedArticles,
    setSelectedArticles,
    hideFilters,
    handlePageChange,
    totalRowsCount,
    totalAmount,
    pageNumber,
    rowsPerPage,
    handleReset,
    submitForm,
    weekBeforeToday,
    today,
    isPaymentsModalOpen,
    isCollectionsModalOpen,
    isExpensesModalOpen,
    setIsPaymentsModalOpen,
    setIsCollectionsModalOpen,
    setIsExpensesModalOpen,
    addPaymentsForm,
    paymentAccounts,
    addCollectionsForm,
    addCExpensesForm,
    submitPayment,
    isModalLoading,
    submitCollection,
    submitExpense,
    selectedPaymentAccountId,
    setSelectedPaymentAccountId,
    rowSelection,
    selectionCount,
    isArchivingModalOpen,
    setIsArchivingModalOpen,
    deletionLoader,
    deleteRows,
    MassActionButton,
    hasUserRightForModuleView,
    hasUserRightToCreateExpense,
    hasUserRightToCreateCollection,
    hasUserRightToToppingUpRegister,
    initialSearch,
    hideToolbar,
    handleFileClick,
    handleFileOpen,
    usersSelectOptions,
    paymentAccountsSelectOptions,
    legalEntitiesSelectOptions,
    expenseArticlesSelectOptions,
    filtersData,
    setIsModalLoading,
    getPaymentAccountsForSystemHandler,
    selectedPaymentSystems,
    setSelectedPaymentSystems,
    hasAccessRightsToPaymentSystemsSelector: onlyAnatolyAccess,
    handlePaymentInfoModalOpen,
    objectId,
    setObjectId,
    paymentInfoModalOpen,
    setPaymentInfoModalOpen,
  };
};
